import { presenceApi } from './presenceClient';
import { presenceActions } from './presenceStore';

/**
 * Reference-counted presence subscription.
 *
 * Every avatar that wants presence calls `watchPresence([id])`. Requests are
 * ref-counted and coalesced into ONE gateway subscription for the union of ids,
 * re-negotiated at most once per animation frame. Scrolling a virtualised
 * member list therefore costs one subscription update, not one per row.
 */
const refCounts = new Map<string, number>();
let unsubscribe: (() => void) | null = null;
let flushHandle: number | null = null;
let subscribedKey = '';

function flush(): void {
  flushHandle = null;
  const ids = [...refCounts.keys()].sort();
  const key = ids.join(',');
  if (key === subscribedKey) return;
  subscribedKey = key;

  unsubscribe?.();
  unsubscribe = null;

  if (ids.length === 0) {
    presenceActions.setConnected(false);
    return;
  }

  /*
   * THE COLD READ BELONGS TO `subscribe`, AND THE ORDER IS THE WHOLE POINT.
   *
   * This used to `fetchMany` here and subscribe afterwards, which left a window
   * — one request long — in which a `presence.update` published by the gateway
   * reached nobody. The gateway publishes exactly one such frame at connect
   * time, from `onConnect`, so the window was hit on the very first load of
   * every session; and because presence frames are sent only on CHANGE, nothing
   * corrected the lost one until the next change, which for somebody sitting
   * still is the auto-away flip minutes later.
   *
   * `PresenceApi.subscribe` now owns both halves and does them in the order that
   * cannot lose (see presenceHttp.ts), so there is nothing to fetch here.
   */
  unsubscribe = presenceApi.subscribe(ids, presenceActions.applyDeltas);
  presenceActions.setConnected(true);
}

function scheduleFlush(): void {
  if (flushHandle !== null) return;
  flushHandle = window.requestAnimationFrame(flush);
}

export function watchPresence(userIds: readonly string[]): () => void {
  for (const id of userIds) refCounts.set(id, (refCounts.get(id) ?? 0) + 1);
  scheduleFlush();

  return () => {
    for (const id of userIds) {
      const next = (refCounts.get(id) ?? 1) - 1;
      if (next <= 0) refCounts.delete(id);
      else refCounts.set(id, next);
    }
    scheduleFlush();
  };
}
